import {
    createListenerMiddleware,
    isFulfilled,
    isRejected,
    isAnyOf,
} from '@reduxjs/toolkit';

import * as Sentry from '@sentry/react';
import { notificationRegistry } from './createAppAsyncThunk';
import { NotificationActions } from '../common/actions/NotificationActions';
import InfoActions from '../actions/InfoActions';
import ContactActions from '../actions/ContactActions';
import UserActions from '../actions/UserActions';
import AdminUsersSlice from '../admin/slices/AdminUsersSlice';
import AdminSortingCriteriaSlice from '../admin/slices/AdminSortingCriteriaSlice';
import AdminInviteSlice from '../admin/slices/AdminInviteSlice';
import ImportService from '../admin/services/ImportService';
import { isSuccessfulResponse } from '../helpers';
import LanguageSlice from '../common/slices/LanguageSlice';
import UserSlice from '../common/slices/UserSlice';
import setLocaleFromUserInfo from '../helpers/setLocaleFromUserInfo';
import { LanguageService } from '../services';
import AuthSelectors from '../selectors/AuthSelectors';
import { AuthService } from '../services/AuthService';

const IMPORT_SUCCESS_MSG = 'fe.admin.import.sucess.title';
const CHANGE_PASSWORD_SUCCESS_MSG = 'fe.change-password.success';

const ADMIN_SORTING_CRITERIA_ERRORS: Record<string, string> = {
    updateCriterion: 'fe.admin.error.update-criterion',
    moveCriterion: 'fe.admin.error.update-criterion',
    createCriterion: 'fe.admin.error.create-criterion',
    deleteCriterion: 'fe.admin.error.delete-criterion',
    moveValue: 'fe.admin.error.update-value',
};

function getTypePrefix(actionType: string, suffix: string): string {
    if (actionType.endsWith(suffix)) {
        return actionType.slice(0, -suffix.length);
    }

    return '';
}

export const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
    matcher: isRejected,
    effect: (action, listenerApi) => {
        if (
            action.meta?.aborted ||
            (action as { error?: { name?: string } }).error?.name ===
                'CanceledError'
        ) {
            return;
        }

        const typePrefix = getTypePrefix(action.type, '/rejected');
        const config = notificationRegistry.get(typePrefix);

        if (!config?.error) {
            return;
        }

        const msg =
            typeof config.error === 'function'
                ? config.error(action)
                : config.error;

        listenerApi.dispatch(NotificationActions.error(msg));
    },
});

listenerMiddleware.startListening({
    matcher: isFulfilled,
    effect: (action, listenerApi) => {
        const typePrefix = getTypePrefix(action.type, '/fulfilled');
        const config = notificationRegistry.get(typePrefix);

        if (!config?.success) {
            return;
        }

        const msg =
            typeof config.success === 'function'
                ? config.success(action)
                : config.success;

        if (msg) {
            listenerApi.dispatch(NotificationActions.success(msg));
        }
    },
});

listenerMiddleware.startListening({
    actionCreator: InfoActions.getInfo.fulfilled,
    effect: (action, listenerApi) => {
        const data = action.payload?.data as
            { languages?: string[] } | undefined;
        const languages = data?.languages;

        if (!languages) {
            return;
        }

        const locale = LanguageSlice.state.getLocale(listenerApi.getState());

        if (!locale) {
            listenerApi.dispatch(
                LanguageSlice.actions.changeLocale(
                    LanguageService.getCurrentLocale(languages),
                ),
            );
        }

        listenerApi.dispatch(
            LanguageSlice.actions.changeAllowedLocales(languages),
        );
    },
});

listenerMiddleware.startListening({
    actionCreator: InfoActions.getInfo.fulfilled,
    effect: (action, listenerApi) => {
        const data = action.payload?.data as
            { companyId?: number | null } | undefined;

        if (data?.companyId == null) {
            return;
        }

        const accessToken = AuthSelectors.getAuthToken(listenerApi.getState());
        const userId = AuthService.getKey(accessToken, 'sub');

        Sentry.setUser({ id: userId, companyId: data.companyId });
    },
});

listenerMiddleware.startListening({
    actionCreator: AdminInviteSlice.actions.sendInvite.fulfilled,
    effect: (action, listenerApi) => {
        const payload = action.payload as { status?: number } | undefined;
        const status = payload?.status;
        if (typeof status === 'number' && isSuccessfulResponse(status)) {
            listenerApi.dispatch(
                NotificationActions.success(
                    'fe.admin.invitations.success.sent',
                ),
            );
        }
    },
});

listenerMiddleware.startListening({
    actionCreator: ContactActions.sendRequest.fulfilled,
    effect: (action, listenerApi) => {
        const data = action.payload?.data as { message?: string } | undefined;
        const message = data?.message ?? '';

        listenerApi.dispatch(NotificationActions.success(message));
    },
});

// RTK 2.13 types `action` as plain `Action` when the matcher is created inline.
const isImportProgressFulfilled = isAnyOf(
    AdminUsersSlice.actions.getImportProgress.fulfilled,
    AdminSortingCriteriaSlice.actions.getImportProgress.fulfilled,
);

listenerMiddleware.startListening({
    matcher: isImportProgressFulfilled,
    effect: (action, listenerApi) => {
        const payload = action.payload as
            | {
                  data?: { status?: string; errors?: unknown[] };
              }
            | undefined;
        const data = payload?.data;

        if (data?.status === 'completed') {
            ImportService.deleteImportId();
            listenerApi.dispatch(
                NotificationActions.success(IMPORT_SUCCESS_MSG),
            );
        }
    },
});

listenerMiddleware.startListening({
    actionCreator: UserActions.changePassword.fulfilled,
    effect: (action, listenerApi) => {
        const payload = action.payload as { status?: number } | undefined;
        const status = payload?.status;

        if (status === 204) {
            listenerApi.dispatch(
                NotificationActions.success(CHANGE_PASSWORD_SUCCESS_MSG),
            );
        }
    },
});

listenerMiddleware.startListening({
    matcher: isAnyOf(
        AdminSortingCriteriaSlice.actions.updateCriterion.rejected.match,
        AdminSortingCriteriaSlice.actions.moveCriterion.rejected.match,
        AdminSortingCriteriaSlice.actions.createCriterion.rejected.match,
        AdminSortingCriteriaSlice.actions.deleteCriterion.rejected.match,
        AdminSortingCriteriaSlice.actions.moveValue.rejected.match,
    ),
    effect: (action, listenerApi) => {
        const thunkName = action.type.split('/')[1] ?? '';
        const msg = ADMIN_SORTING_CRITERIA_ERRORS[thunkName];

        if (msg) {
            listenerApi.dispatch(NotificationActions.error(msg));
        }
    },
});

listenerMiddleware.startListening({
    actionCreator: UserSlice.actions.getInfo.fulfilled,
    effect: (action, listenerApi) => {
        const data = action.payload?.data;
        if (!data) {
            return;
        }

        const locale = LanguageSlice.state.getLocale(listenerApi.getState());
        if (locale) {
            const allowedLocales = LanguageSlice.state.getAllowedLocales(
                listenerApi.getState(),
            );
            setLocaleFromUserInfo(
                data,
                locale,
                allowedLocales,
                listenerApi.dispatch,
            );
        }

        listenerApi.dispatch(UserSlice.actions.getActiveQuestionnaires());
    },
});
